import { ApiAvailables, ApiScoreCard, ApiScoreCardv2, ISimulationScore } from '@/types';
import { evaluateScorePass } from './pass-policy';

// ================================
// Module Overview
// ================================

/**
 * # Certification Pass Aggregation Policy
 *
 * Purpose:
 * Centralize certification-level pass aggregation so utility mappers remain
 * responsible for shape/default mapping only.
 *
 * Why this exists:
 * - Avoids embedding cross-entity business logic inside mapper utilities.
 * - Keeps pass/fail rule application in the scoring domain.
 * - Supports consistent behavior across v1/v2 scorecard payloads.
 *
 * Scope:
 * - Accepts one certification available context.
 * - Consumes scorecards via an injected mapper dependency.
 * - Resolves one boolean certification pass result.
 */

// ================================
// Types
// ================================

/**
 * Dependency contract so this policy stays pure/testable and does not directly
 * depend on utility-layer implementations.
 */
export interface ICertificationPassPolicyDependencies {
  /**
   * Maps scorecards into frontend score entities for pass aggregation.
   *
   * Note:
   * Dependency injection is intentional so this policy can stay pure and
   * unit-testable without direct imports from utility-layer mapper modules.
   */
  /** Maps scorecards into frontend score entities for pass aggregation. */
  mapApiScorecardsToFrontend: (params: {
    apiAvailables: ApiAvailables[];
    apiScorecards: (ApiScoreCard | ApiScoreCardv2)[];
  }) => ISimulationScore[];
}

/**
 * Input contract for certification pass aggregation.
 */
export interface IResolveCertificationPassParams {
  /**
   * Certification available payload currently being mapped.
   *
   * This provides both the certification context and simulation type context
   * used for pass applicability.
   */
  /** Certification available payload currently being mapped. */
  available: ApiAvailables;

  /**
   * Scorecards in mixed v1/v2 API formats.
   *
   * The policy itself does not care about version-specific payload structure;
   * it delegates normalization to the injected mapper dependency.
   */
  /** Scorecards in mixed v1/v2 API formats. */
  apiScorecards: (ApiScoreCard | ApiScoreCardv2)[];

  /**
   * Required dependency bundle.
   *
   * Keeping this explicit makes testing and future composition straightforward.
   */
  /** Required dependency bundle. */
  dependencies: ICertificationPassPolicyDependencies;
}

// ================================
// Public Domain Policy
// ================================

/**
 * Resolves pass/fail status for one certification context.
 *
 * Policy:
 * 1) Map scorecards scoped to this available.
 * 2) Pick certification-typed score if present.
 * 3) Evaluate pass/fail via domain pass policy.
 * 4) Preserve existing mapper behavior by coercing indeterminate to false.
 */
export const resolveCertificationPass = ({
  available,
  apiScorecards,
  dependencies,
}: IResolveCertificationPassParams): boolean => {
  // Step 1: Map scorecards in the scope of this single available.
  // This preserves existing behavior where certification resolution happens
  // against the current available context only.
  const scorecards = dependencies.mapApiScorecardsToFrontend({
    apiAvailables: [available],
    apiScorecards,
  });

  // Step 2: Select the certification-typed score when present.
  // If no certification score exists, aggregation becomes indeterminate and is
  // handled by fallback in Step 4.
  const certificationScore = scorecards.find((score) => {
    return score.simulationType === 'certification';
  });

  // Step 3: Evaluate pass/fail using domain pass policy.
  // `certPass` is intentionally unset here because we want evaluation to rely
  // on the mapped score object and simulation context.
  const pass = evaluateScorePass({
    certPass: undefined,
    score: certificationScore ?? null,
    simulationType: available?.certificationSimulation?.type,
  });

  // Step 4: Preserve historical mapper behavior by coercing indeterminate
  // values (`null`) to `false` at this aggregation boundary.
  return pass ?? false;
};
